// Audity u zákazníků (F11 11.5; kontrakt tag Audity `/audity*`, `/audity-sablony*`, veřejně tag Verejne
// `/verejne/audit-podklady/{token}`):
//   • seznam s čipy (moje, nepřidělené, ke kontrole, otevřené, vše) a Můj den auditora (moje, nepřidělené — Vzít si,
//     ke kontrole pro vedoucího);
//   • detail (karta + odpovědi, soubory k otázkám, chybějící podklady, protokol, nápravná opatření, fakturační
//     položky, opravy vedoucího, odkaz pro podklady, PDF v archivu) a šablona dotazníku (sekce, otázky, podmínky);
//   • přechody: naplánovat, vzít si, zahájit, pauza, ukončit podpisem zástupce, zástupce nepodepsal (důvod), podpis
//     auditora, předat ke kontrole, vrátit s komentářem, schválit, odeslat k fakturaci; tisk protokolu a reportu;
//   • soubory k otázkám (POST /prilohy s entitou audit_dotaznik → připojit k otázce) a odkaz pro podklady;
//   • veřejná stránka podkladů bez přihlášení.
// Měřený čas, cenu i práva `muze` s důvody počítá server — UI jen kreslí a posílá.
import type { Schema } from '@ecoser/shared';
import { keepPreviousData, type QueryClient, useQuery } from '@tanstack/react-query';
import { api } from './klient';

export type AuditKarta = Schema<'AuditKarta'>;
export type AuditDetail = Schema<'AuditDetail'>;
export type AuditMuze = Schema<'AuditMuze'>;
export type AuditSoubor = Schema<'AuditSoubor'>;
export type AuditCena = Schema<'AuditCena'>;
export type AuditVstup = Schema<'AuditVstup'>;
export type AuditZmena = Schema<'AuditZmena'>;
export type AuditSeznam = Schema<'AuditSeznam'>;
export type AuditMujDen = Schema<'AuditMujDen'>;
export type AuditPdf = Schema<'AuditPdf'>;
export type AuditPodkladyOdkaz = Schema<'AuditPodkladyOdkaz'>;
export type AuditSablona = Schema<'AuditSablonaVerzeDetail'>;
export type AuditFirma = Schema<'AuditFirma'>;
export type AuditFirmySeznam = Schema<'AuditFirmySeznam'>;
export type AuditPrideleniVysledek = Schema<'AuditPrideleniVysledek'>;
export type VerejnePodkladyAuditu = Schema<'VerejnePodkladyAuditu'>;
export type StavAuditu = AuditKarta['stav'];
export type SkupinaAuditu = 'vse' | 'moje' | 'nepridelene' | 'ke_kontrole' | 'otevrene';
export type SekceSablony = AuditSablona['sekce'][number];
export type OtazkaSablony = SekceSablony['otazky'][number];
export type Odpovedi = Record<string, unknown>;

export const AKCE_AUDIT_NAPLANOVAT = 'audit.naplanovat';
export const AKCE_AUDIT_PROVEST = 'audit.provest';
export const AKCE_AUDIT_SCHVALIT = 'audit.schvalit';
export const AKCE_AUDIT_PRIDELIT_FIRMY = 'audit.pridelit_firmy';

export const KLIC_AUDITU = ['audity'] as const;
/** Přípona klíče „podklad k otázce dodá zákazník dodatečně“ (backend sablona.ts `PRIPONA_DODA`). */
export const PRIPONA_DODA = '_doda';

/**
 * Cesta detailu auditu v sekci Audity (backend typy.ts `ODKAZ_AUDITU`, odkazy signálů a zvonku). Od 24. 10. 2026
 * (W80) má audit vlastní sekci; staré odkazy `/zakazky/audit/…` přesměruje App.tsx.
 */
export const cestaAuditu = (id: string) => `/audity/${encodeURIComponent(id)}`;
export const CESTA_NOVY_AUDIT = '/audity/novy';
export const CESTA_AUDITU = '/audity';

/**
 * Detail auditu podle sekcí účtu: se sekcí Audity `/audity/{id}`, jinak (obchodník — audity svých zákazníků, 05/3951 a)
 * přes sekci Zakázky `/zakazky/audit/{id}`; bez obou `null` (odkaz se nevykreslí).
 */
export function cestaAudituPro(
  ja: { sekce?: readonly string[] } | null | undefined,
  id: string,
): string | null {
  const sekce = ja?.sekce ?? [];
  if (sekce.includes('audity')) return cestaAuditu(id);
  if (sekce.includes('zakazky')) return `/zakazky/audit/${encodeURIComponent(id)}`;
  return null;
}

const enc = encodeURIComponent;

/** Barvy štítku stavu (prototyp `auditStavMeta` H:43518 — tokeny 04, žádné nové barvy). */
export const TON_STAVU: Readonly<
  Record<AuditKarta['stitek'], 'neutral' | 'info' | 'warn' | 'danger' | 'ok'>
> = {
  koncept: 'neutral',
  naplanovany: 'info',
  bez_auditora: 'warn',
  probiha: 'warn',
  na_zpracovani: 'info',
  ke_kontrole: 'warn',
  vraceno: 'danger',
  schvaleno: 'ok',
  odeslano: 'ok',
};

export interface FiltrAuditu {
  skupina?: SkupinaAuditu;
  hledat?: string;
  zakaznik_id?: string;
  rok?: number;
  velikost?: number;
  /** Audity v rozsahu dnů (Můj den kanceláře › Auditoři, W64). */
  od?: string;
  do?: string;
  /** Přehled auditů (24. 10. 2026): auditor, stav, po termínu, bez auditora, stránka. */
  auditor_id?: string;
  stav?: StavAuditu;
  po_terminu?: boolean;
  bez_auditora?: boolean;
  /** Bez stornovaných — počty v přehledu Podle auditorů je nepočítají (odkaz z buňky přehledu). */
  bez_storna?: boolean;
  /** Řazení `pole:asc|desc` (cislo, firma, datum, auditor, stav); bez něj nejnovější den auditu. */
  razeni?: string;
  strana?: number;
}

function dotaz(f: FiltrAuditu): string {
  const p = new URLSearchParams();
  p.set('velikost', String(f.velikost ?? 100));
  if (f.skupina) p.set('skupina', f.skupina);
  if (f.hledat?.trim()) p.set('hledat', f.hledat.trim());
  if (f.zakaznik_id) p.set('zakaznik_id', f.zakaznik_id);
  if (f.rok) p.set('rok', String(f.rok));
  if (f.od) p.set('od', f.od);
  if (f.do) p.set('do', f.do);
  if (f.auditor_id) p.set('auditor_id', f.auditor_id);
  if (f.stav) p.set('stav', f.stav);
  if (f.po_terminu) p.set('po_terminu', 'true');
  if (f.bez_auditora) p.set('bez_auditora', 'true');
  if (f.bez_storna) p.set('bez_storna', 'true');
  if (f.razeni) p.set('razeni', f.razeni);
  if (f.strana) p.set('strana', String(f.strana));
  return p.toString();
}

export function useAudity(f: FiltrAuditu, povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_AUDITU, 'seznam', f],
    queryFn: ({ signal }) => api.get<AuditSeznam>(`/audity?${dotaz(f)}`, { signal }),
    placeholderData: keepPreviousData,
    enabled: povoleno,
  });
}

export function useMujDenAuditora(povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_AUDITU, 'muj-den'],
    queryFn: ({ signal }) => api.get<AuditMujDen>('/audity/muj-den', { signal }),
    enabled: povoleno,
    // Běžící čas na místě přepočítá server — stačí jednou za minutu.
    refetchInterval: 60_000,
  });
}

export function useAudit(id: string | undefined) {
  return useQuery({
    queryKey: [...KLIC_AUDITU, 'detail', id],
    queryFn: ({ signal }) => api.get<AuditDetail>(`/audity/${enc(id ?? '')}`, { signal }),
    enabled: Boolean(id),
    refetchInterval: (q) => (q.state.data?.stav === 'probiha' ? 60_000 : false),
  });
}

export function useSablonaAuditu(id: string | undefined) {
  return useQuery({
    queryKey: [...KLIC_AUDITU, 'sablona', id],
    queryFn: ({ signal }) => api.get<AuditSablona>(`/audity-sablony/${enc(id ?? '')}`, { signal }),
    enabled: Boolean(id),
    staleTime: Infinity,
  });
}

/** Auditovaná firma pro formulář (GET /audity/zakaznici — auditor sekci Zákazníci nevidí). */
export interface FirmaAuditu {
  id: string;
  nazev: string;
  ico?: string;
  obec?: string;
  /** Trvale přidělený auditor firmy (24. 10. 2026) — formulář naplánování ho předvyplní. */
  auditor?: { id: string; jmeno: string };
  provozovny: Array<{ id: string; nazev: string; adresa: string; icp?: string }>;
  kontakty: Array<{ id: string; jmeno: string }>;
}

export async function hledatFirmyAuditu(text: string, signal?: AbortSignal) {
  const r = await api.get<{ polozky: FirmaAuditu[] }>(
    `/audity/zakaznici?hledat=${enc(text.trim())}`,
    { signal },
  );
  return { polozky: r.polozky, celkem: r.polozky.length };
}

/** Firma auditu podle id (po založení zákazníka, provozovny nebo kontaktu přímo z plánu auditu — W62 díl 2). */
export async function nacistFirmuAuditu(id: string): Promise<FirmaAuditu | null> {
  return (
    (await api.get<{ polozky: FirmaAuditu[] }>(`/audity/zakaznici?id=${enc(id)}`)).polozky[0] ??
    null
  );
}

export function useFirmaAuditu(id: string | undefined) {
  return useQuery({
    queryKey: [...KLIC_AUDITU, 'firma', id],
    queryFn: async ({ signal }) =>
      (
        await api.get<{ polozky: FirmaAuditu[] }>(`/audity/zakaznici?id=${enc(id ?? '')}`, {
          signal,
        })
      ).polozky[0] ?? null,
    enabled: Boolean(id),
  });
}

/** Firmy a jejich auditoři (24. 10. 2026): auditor jen své firmy, kdo přiděluje, všechny s filtry. */
export interface FiltrFirem {
  hledat?: string;
  auditor_id?: string;
  zakaznik_id?: string;
  bez_auditora?: boolean;
  sjednane?: boolean;
  strana?: number;
  velikost?: number;
}

export function useFirmyAuditu(f: FiltrFirem, povoleno = true) {
  const p = new URLSearchParams();
  p.set('velikost', String(f.velikost ?? 100));
  if (f.strana) p.set('strana', String(f.strana));
  if (f.hledat?.trim()) p.set('hledat', f.hledat.trim());
  if (f.auditor_id) p.set('auditor_id', f.auditor_id);
  if (f.zakaznik_id) p.set('zakaznik_id', f.zakaznik_id);
  if (f.bez_auditora !== undefined) p.set('bez_auditora', String(f.bez_auditora));
  if (f.sjednane) p.set('sjednane', 'true');
  return useQuery({
    queryKey: [...KLIC_AUDITU, 'firmy', f],
    queryFn: ({ signal }) => api.get<AuditFirmySeznam>(`/audity/firmy?${p.toString()}`, { signal }),
    placeholderData: keepPreviousData,
    enabled: povoleno,
  });
}

export type AuditPodleAuditoru = Schema<'AuditPodleAuditoru'>;
export type AuditTyp = Schema<'AuditTyp'>;
export type AuditTypVstup = Schema<'AuditTypVstup'>;
export type AuditTypZmena = Schema<'AuditTypZmena'>;
export type AuditSablonaVerze = Schema<'AuditSablonaVerze'>;
export const AKCE_AUDIT_SPRAVOVAT_SABLONY = 'audit.spravovat_sablony';

/** Typy auditů (migrace 5162): plán auditu dostane aktivní s vydanou šablonou, správce šablon všechny. */
export function useTypyAuditu(povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_AUDITU, 'typy'],
    queryFn: ({ signal }) =>
      api.get<{ polozky: AuditTyp[]; muze: { spravovat: { smi: boolean } } }>('/audity-typy', {
        signal,
      }),
    enabled: povoleno,
    staleTime: 60_000,
  });
}

export function useVerzeSablon(typId: string | undefined) {
  return useQuery({
    queryKey: [...KLIC_AUDITU, 'sablony', typId],
    queryFn: ({ signal }) =>
      api.get<{ polozky: AuditSablonaVerze[] }>(`/audity-sablony?typ_id=${enc(typId ?? '')}`, {
        signal,
      }),
    enabled: Boolean(typId),
  });
}

export const zalozitTypAuditu = (telo: AuditTypVstup) => api.post<AuditTyp>('/audity-typy', telo);
export const upravitTypAuditu = (id: string, telo: AuditTypZmena) =>
  api.put<AuditTyp>(`/audity-typy/${enc(id)}`, telo);
export const novaVerzeSablony = (typId: string, zVerzeId?: string) =>
  api.post<AuditSablona>('/audity-sablony', {
    typ_id: typId,
    ...(zVerzeId ? { z_verze_id: zVerzeId } : {}),
  });
export const upravitSablonu = (
  id: string,
  telo: { nazev?: string; poznamka?: string | null; platnost_od?: string; sekce?: SekceSablony[] },
) => api.put<AuditSablona>(`/audity-sablony/${enc(id)}`, telo);
export const vydatSablonu = (id: string) =>
  api.post<AuditSablona>(`/audity-sablony/${enc(id)}/vydat`, {});
export const smazatSablonu = (id: string) => api.delete<void>(`/audity-sablony/${enc(id)}`);
/** Editor šablony v sekci Audity. */
export const cestaEditoruSablony = (id: string) => `/audity/sablony/${enc(id)}`;

/** Počty auditů podle auditorů za období (přehled vedoucího, 24. 10. 2026) — čísla počítá server. */
export function usePodleAuditoru(od: string | undefined, do_: string | undefined, povoleno = true) {
  const p = new URLSearchParams();
  if (od) p.set('od', od);
  if (do_) p.set('do', do_);
  return useQuery({
    queryKey: [...KLIC_AUDITU, 'podle-auditoru', od, do_],
    queryFn: ({ signal }) =>
      api.get<AuditPodleAuditoru>(`/audity/podle-auditoru?${p.toString()}`, { signal }),
    placeholderData: keepPreviousData,
    enabled: povoleno,
  });
}

export const pridelitFirmy = (zakaznikIds: string[], auditorId: string | null) =>
  api.post<AuditPrideleniVysledek>('/audity/firmy/pridelit', {
    zakaznik_ids: zakaznikIds,
    auditor_id: auditorId,
  });

export interface AuditorVyber {
  id: string;
  jmeno: string;
  role: string;
}

/** Komu jde audit přidělit; s typem auditu jen auditoři, kteří typ smějí provádět (migrace 5162). */
export function useAuditori(povoleno = true, typId?: string) {
  return useQuery({
    queryKey: [...KLIC_AUDITU, 'auditori', typId ?? null],
    queryFn: ({ signal }) =>
      api.get<{ polozky: AuditorVyber[] }>(
        `/audity/auditori${typId ? `?typ_id=${enc(typId)}` : ''}`,
        { signal },
      ),
    enabled: povoleno,
    staleTime: 5 * 60_000,
  });
}

export const zalozitAudit = (telo: AuditVstup) => api.post<AuditDetail>('/audity', telo);
export const upravitAudit = (id: string, telo: AuditZmena) =>
  api.put<AuditDetail>(`/audity/${enc(id)}`, telo);

const prechod = (id: string, akce: string, telo?: unknown) =>
  api.post<AuditDetail>(`/audity/${enc(id)}/${akce}`, telo ?? {});

export const naplanovatAudit = (id: string, datum: string, auditorId?: string | null) =>
  prechod(id, 'naplanovat', {
    datum,
    ...(auditorId !== undefined ? { auditor_id: auditorId } : {}),
  });
export const vzitSiAudit = (id: string) => prechod(id, 'vzit-si');
export const zahajitAudit = (id: string) => prechod(id, 'zahajit');
export const pauzaAuditu = (id: string, akce: 'start' | 'konec') => prechod(id, 'pauza', { akce });
export interface PodpisAuditu {
  jmeno: string;
  obraz_png_base64: string;
  kontakt_id?: string;
}
export const ukoncitPodpisem = (id: string, p: PodpisAuditu) =>
  prechod(id, 'ukoncit-podpisem', { ...p, zpusob: 'tablet', souhlas_potvrzen: true });
export const vynechatPodpisZastupce = (id: string, duvod: string) =>
  prechod(id, 'vynechat-podpis-zastupce', { duvod });
export const podepsatAuditor = (id: string, p: Omit<PodpisAuditu, 'kontakt_id'>) =>
  prechod(id, 'podepsat-auditor', { ...p, zpusob: 'tablet', souhlas_potvrzen: true });
export const predatKeKontrole = (id: string) => prechod(id, 'predat-ke-kontrole');
export const vratitAudit = (id: string, komentar: string) => prechod(id, 'vratit', { komentar });
export const schvalitAudit = (id: string) => prechod(id, 'schvalit');
export const odeslatAudit = (id: string) => prechod(id, 'odeslat');

export const pripojitSoubor = (id: string, otazkaKlic: string, prilohaId: string) =>
  api.post<AuditSoubor>(`/audity/${enc(id)}/soubory`, {
    otazka_klic: otazkaKlic,
    priloha_id: prilohaId,
  });
export const odebratSoubor = (id: string, vazbaId: string) =>
  api.delete<void>(`/audity/${enc(id)}/soubory/${enc(vazbaId)}`);
export const odkazProPodklady = (id: string, email?: string) =>
  api.post<AuditPodkladyOdkaz>(`/audity/${enc(id)}/podklady-odkaz`, email ? { email } : {});

export type DruhTisku = 'protokol' | 'report';
export const posledniPdf = (id: string, druh: DruhTisku) =>
  api.get<AuditPdf>(`/audity/${enc(id)}/${druh}-pdf`);
export const vytisknoutPdf = (id: string, druh: DruhTisku) =>
  api.post<AuditPdf>(`/audity/${enc(id)}/${druh}-pdf`, {});

/** Po zápisu: seznamy, Můj den, detail, zakázky (audit je zakázka) a nápravná opatření / kalendář. */
export function obnovitAudity(qc: QueryClient): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: KLIC_AUDITU }),
    qc.invalidateQueries({ queryKey: ['zakazky'] }),
    qc.invalidateQueries({ queryKey: ['iso-napravna-opatreni'] }),
    qc.invalidateQueries({ queryKey: ['kalendar'] }),
  ]).then(() => undefined);
}

// --- Šablona a odpovědi (zobrazení stejně jako server — sablona.ts) ----------------------------------------------

/** Otázka se ukáže, když platí kterákoli podmínka (bez podmínek vždy). */
export function otazkaViditelna(
  o: Pick<OtazkaSablony, 'zobrazitPokud'>,
  odpovedi: Odpovedi,
): boolean {
  if (!o.zobrazitPokud || o.zobrazitPokud.length === 0) return true;
  return o.zobrazitPokud.some((p) => {
    const v = odpovedi[p.klic];
    return typeof v === 'string' && p.in.includes(v);
  });
}

export function prazdnaOdpoved(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
}

/** Odpověď pro čtení v detailu: hodnocení hvězdičkami, km s jednotkou. */
export function textOdpovedi(o: OtazkaSablony, v: unknown): string {
  if (prazdnaOdpoved(v)) return '—';
  if (o.typ === 'hodnoceni' && typeof v === 'number')
    return `${'★'.repeat(v)}${'☆'.repeat(Math.max(0, 5 - v))}`;
  if (typeof v === 'number') return String(v).replace('.', ',');
  return String(v);
}

// --- Veřejná stránka podkladů (bez přihlášení) -------------------------------------------------------------------

export function useVerejnePodklady(token: string | undefined) {
  return useQuery({
    queryKey: ['verejne-podklady-auditu', token],
    queryFn: ({ signal }) =>
      api.get<VerejnePodkladyAuditu>(`/verejne/audit-podklady/${enc(token ?? '')}`, {
        signal,
        bezHlaseniSite: true,
      }),
    enabled: Boolean(token),
    retry: false,
  });
}

export const nahratPodklad = (
  token: string,
  telo: { otazka_klic: string; nazev: string; mime: string; obsah_base64: string },
) =>
  api.post<{ nazev: string; otazka_klic: string; ulozeno: boolean }>(
    `/verejne/audit-podklady/${enc(token)}/soubory`,
    telo,
    { bezHlaseniSite: true },
  );
